import {
  buildNormalizedDay,
  buildNormalizedPilotBlueprint,
  localizedGenderNode,
} from "./referenceNormalizerPilotUtils";

/** Subcadena requerida por scripts/validate_dna_blueprints.mjs (pasos de escritura). */
export const DNA_VALIDATE_WRITING_PHRASE = "אני שואל/ת את עצמי בשקט";

const days = [
  {
    dayNumber: 1,
    emotionalStage: "pause",
    title: "Nombrar la voz que me achica",
    opening: localizedGenderNode([
      "Hay una voz en mí que no siempre grita.",
      "A veces solo me baja un poco.",
      "Un poco menos digno.",
      "Un poco menos suficiente.",
      "Un poco menos valioso.",
      "Y casi sin darme cuenta,",
      "ya le creo.",
    ]),
    mindfulness: localizedGenderNode([
      "Un momento.",
      "Ahora no discuto con la voz.",
      "Solo noto que ya está aquí.",
    ]),
    prompts: localizedGenderNode([
      "Los momentos en que esa voz ya está aquí, dentro.",
      "Una frase que vuelve en mí sin que yo la elija.",
      "En el cuerpo, cuando ya le creo — algo ahí.",
    ]),
    closing: localizedGenderNode([
      "Esta voz ya me suena familiar.",
      "Entra un poco de aire entre ella y yo.",
    ]),
    action: localizedGenderNode(
      "Hoy hago una pausa y digo en silencio: esto es una voz, no todo lo que soy.",
      "Hoy hago una pausa y digo en silencio: esto es una voz, no todo lo que soy."
    ),
  },
  {
    dayNumber: 2,
    emotionalStage: "pain",
    title: "El momento en que mi valor cae",
    opening: localizedGenderNode([
      "A veces pasa rápido.",
      "Una reacción.",
      "Una comparación.",
      "Una mirada.",
      "Y algo en mí ya cae.",
      "Como si mi valor dependiera de este momento.",
    ]),
    mindfulness: localizedGenderNode([
      "Me quedo un momento justo en esa caída.",
      "Antes de que todo se cierre a mi alrededor.",
    ]),
    prompts: localizedGenderNode([
      "El instante antes de sentirme menos — algo ahí.",
      "Lo primero que sube ahí, en mí.",
      "Algo adentro que empuja la caída demasiado rápido.",
    ]),
    closing: localizedGenderNode([
      "Esa caída se me nota por dentro.",
      "Solo me quedé un momento con la observación.",
    ]),
    action: localizedGenderNode(
      "Hoy noto el primer punto en que mi valor empieza a bajar.",
      "Hoy noto el primer punto en que mi valor empieza a bajar."
    ),
  },
  {
    dayNumber: 3,
    emotionalStage: "pain",
    title: "Cómo se sienta en el cuerpo",
    opening: localizedGenderNode([
      "Esta sensación no se queda solo en la cabeza.",
      "El pecho se hunde.",
      "El vientre se cierra.",
      "La mirada baja.",
      "Ocupo menos espacio.",
      "Como si mi cuerpo ya hubiera aceptado el veredicto.",
    ]),
    mindfulness: localizedGenderNode([
      "Siento cómo mi cuerpo sostiene este achicamiento.",
    ]),
    prompts: localizedGenderNode([
      "La sensación de ser menos — dónde se queda en mi cuerpo ahora.",
      "Mi cuerpo ahí — como si cuidara algo.",
      "Un poco de suavidad — respiración, una mano — cómo se siente adentro.",
    ]),
    closing: localizedGenderNode([
      "Mi cuerpo sostiene mucho de esta sensación.",
      "Me quedé un rato con él.",
    ]),
    action: localizedGenderNode(
      "Hoy pongo una mano en el cuerpo y respiro una vez cuando me encojo.",
      "Hoy pongo una mano en el cuerpo y respiro una vez cuando me encojo."
    ),
  },
  {
    dayNumber: 4,
    emotionalStage: "pattern",
    title: "Cómo ya me demuestro que soy menos",
    opening: localizedGenderNode([
      "Empiezo a ver este patrón.",
      "Junto pruebas en mi contra.",
      "Recuerdo más lo que no funcionó.",
      "Achico lo que sí.",
      "Y me armo una historia",
      "que explica por qué soy menos.",
    ]),
    mindfulness: localizedGenderNode([
      "Hago una pausa entre lo que pasó",
      "y cómo ya lo uso en mi contra.",
    ]),
    prompts: localizedGenderNode([
      "La forma en que otra vez me demuestro que soy menos.",
      "Lo que inflo en mi contra sin darme cuenta.",
      "Lo que en mí sí va — y casi no me quedo con eso.",
    ]),
    closing: localizedGenderNode([
      "Vi la forma en que construyo esto.",
      "Ya me cuesta menos no verlo.",
    ]),
    action: localizedGenderNode(
      "Hoy marco un momento en que vuelvo a usar algo en mi contra.",
      "Hoy marco un momento en que vuelvo a usar algo en mi contra."
    ),
  },
  {
    dayNumber: 5,
    emotionalStage: "fear",
    title: "Qué temo que esto diga de mí",
    opening: localizedGenderNode([
      "Debajo de esta caída en cómo siento mi valor,",
      "también hay miedo.",
      "De que si no soy suficiente aquí,",
      "quizá simplemente no soy suficiente.",
      "De que esto no sea un momento.",
      "De que esto sea quien soy.",
    ]),
    mindfulness: localizedGenderNode([
      "Me quedo un momento con el significado que le doy a esto.",
    ]),
    prompts: localizedGenderNode([
      "El miedo que me cuenta qué dice de mí este momento.",
      "Una frase vieja que se enciende ahí dentro.",
      "La parte más ardiente de este miedo.",
    ]),
    closing: localizedGenderNode([
      "Escuché el miedo bajo el achicamiento.",
      "Toca hondo mi sentido de valor.",
    ]),
    action: localizedGenderNode(
      "Hoy le pongo nombre a este miedo una vez, en silencio.",
      "Hoy le pongo nombre a este miedo una vez, en silencio."
    ),
  },
  {
    dayNumber: 6,
    emotionalStage: "inner_world",
    title: "La parte que ya lo cree",
    opening: localizedGenderNode([
      "También hay una parte en mí que se acostumbró.",
      "Que ya cree que soy menos.",
      "Que no espera mucho.",
      "Que no se sorprende cuando vuelvo a cerrarme.",
      "No grita.",
      "Solo lleva mucho tiempo ahí.",
    ]),
    mindfulness: localizedGenderNode([
      "Veo la parte que ya cree esta historia.",
    ]),
    prompts: localizedGenderNode([
      "El lugar en mí que ya cree que soy menos.",
      "Esa parte — cómo roza mis elecciones.",
      "Algo que esa parte me pide sin palabras.",
    ]),
    closing: localizedGenderNode([
      "Hay una parte en mí que sostiene esta creencia hace rato.",
      "Hoy solo la veo un poco más de cerca.",
    ]),
    action: localizedGenderNode(
      "Hoy noto un momento en que esta parte me dirige.",
      "Hoy noto un momento en que esta parte me dirige."
    ),
  },
  {
    dayNumber: 7,
    emotionalStage: "need",
    title: "Lo que realmente necesito debajo de esto",
    opening: localizedGenderNode([
      "Detrás de todo esto",
      "hay también una necesidad simple.",
      "Sentirme con valor.",
      "Sentir que tengo lugar.",
      "Sentir que no tengo que demostrar todo el tiempo",
      "para seguir estando bien.",
    ]),
    mindfulness: localizedGenderNode([
      "Escucho esta necesidad sin menospreciarla.",
    ]),
    prompts: localizedGenderNode([
      "Bajo lo de ser menos — un necesitar ahí, sin pedir en voz alta.",
      "Un poco de firmeza adentro — cómo me suena cuando me detengo.",
      "Un pedirme suave — cómo se siente adentro.",
    ]),
    closing: localizedGenderNode([
      "Hay en mí una necesidad real de sentir valor.",
      "Esa necesidad también tiene un pequeño lugar.",
    ]),
    action: localizedGenderNode(
      "Hoy le doy a esta necesidad un pequeño espacio.",
      "Hoy le doy a esta necesidad un pequeño espacio."
    ),
  },
  {
    dayNumber: 8,
    emotionalStage: "softening",
    title: "No hablarme desde arriba",
    opening: localizedGenderNode([
      "Cuando me siento menos,",
      "mi tono conmigo se vuelve frío.",
      "Anulando.",
      "Desde arriba.",
      "Como si ya fuera un fracaso claro.",
      "Y eso me baja aún más.",
    ]),
    mindfulness: localizedGenderNode([
      "Escucho cómo me hablo dentro de esta caída.",
    ]),
    prompts: localizedGenderNode([
      "Mi tono conmigo cuando ya me siento menos.",
      "Palabras que me faltan ahí, bajo el tono frío.",
      "Una frase que me achica menos — y sigue sintiéndose verdadera adentro.",
    ]),
    closing: localizedGenderNode([
      "Mi tono cambia toda la profundidad de la caída.",
      "Se queda así conmigo.",
    ]),
    action: localizedGenderNode(
      "Hoy me digo una frase que no me hable desde arriba.",
      "Hoy me digo una frase que no me hable desde arriba."
    ),
  },
  {
    dayNumber: 9,
    emotionalStage: "perspective",
    title: "No toda vacilación es prueba",
    opening: localizedGenderNode([
      "Quizá no toda duda significa que soy débil.",
      "No todo error significa que soy menos.",
      "No toda dificultad es prueba en mi contra.",
      "A veces salto demasiado rápido",
      "de lo que pasó",
      "a lo que dice eso de mí.",
    ]),
    mindfulness: localizedGenderNode([
      "Vuelvo a lo que pasó de verdad",
      "y no solo a lo que ya concluí sobre mí.",
    ]),
    prompts: localizedGenderNode([
      "Lo que pasó — al lado de lo que el sentimiento ya me contó de mí.",
      "La conclusión a la que salto demasiado rápido sobre mí.",
      "Otra posibilidad pequeña — solo presente ahí, sin decidir.",
    ]),
    closing: localizedGenderNode([
      "Algo más estuvo conmigo junto a lo que pasó.",
      "Un poco menos de dureza por dentro.",
    ]),
    action: localizedGenderNode(
      "Hoy sostengo una posibilidad más además de \"soy menos\".",
      "Hoy sostengo una posibilidad más además de \"soy menos\"."
    ),
  },
  {
    dayNumber: 10,
    emotionalStage: "choice",
    title: "El momento antes de achicarme",
    opening: localizedGenderNode([
      "Hay un momento en que ya lo hago solo.",
      "Me achico antes de que nadie haya dicho nada.",
      "Renuncio de entrada.",
      "Me encojo de entrada.",
      "Y quiero ver ese momento.",
    ]),
    mindfulness: localizedGenderNode([
      "Hago una pausa antes de volver a bajarme por dentro.",
    ]),
    prompts: localizedGenderNode([
      "El achicarse que llega antes de que pase algo.",
      "Algo que intento cuidar cuando me achico de entrada.",
      "Antes del automático de achicarme — un instante que noto en mí.",
    ]),
    closing: localizedGenderNode([
      "Vi este achicamiento temprano.",
      "Eso ya me da una pequeña elección.",
    ]),
    action: localizedGenderNode(
      "Hoy me quedo un instante más antes de achicarme.",
      "Hoy me quedo un instante más antes de achicarme."
    ),
  },
  {
    dayNumber: 11,
    emotionalStage: "perspective",
    title: "Ver también algo vivo en mí",
    opening: localizedGenderNode([
      "Cuando estoy dentro de esta sensación,",
      "solo veo lo que me falta.",
      "Pero también hay cosas en mí que están vivas.",
      "Cosas que se quedan.",
      "Cosas que no desaparecen solo porque pasé un momento duro.",
    ]),
    mindfulness: localizedGenderNode([
      "Me quedo un momento con lo que sigue vivo en mí—aunque no me sienta fuerte.",
    ]),
    prompts: localizedGenderNode([
      "Algo que sigue vivo en mí, incluso en un día así.",
      "Una parte en mí a la que casi no le doy lugar cuando mi valor baja.",
      "Algo que traigo conmigo — aunque lo olvide.",
    ]),
    closing: localizedGenderNode([
      "Recordé que hay más en mí de lo que siento en una caída.",
      "Me importa recordarlo.",
    ]),
    action: localizedGenderNode(
      "Hoy sostengo una cosa viva en mí que no depende del rendimiento de hoy.",
      "Hoy sostengo una cosa viva en mí que no depende del rendimiento de hoy."
    ),
  },
  {
    dayNumber: 12,
    emotionalStage: "strength",
    title: "Quedarme con algo en mí que no me quitaron",
    opening: localizedGenderNode([
      "Quizá mi valor no tiene que subir y bajar con cada reacción.",
      "Quizá hay algo en mí que sigue siendo mío",
      "aunque esté confundido.",
      "aunque caiga.",
      "aunque no tenga prueba desde afuera.",
    ]),
    mindfulness: localizedGenderNode([
      "Me quedo un momento con lo que en mí no me quitan tan rápido.",
    ]),
    prompts: localizedGenderNode([
      "Algo que sigue siendo mío sin visto bueno de afuera.",
      "Momentos en que me es más fácil recordarlo.",
      "Cuando mi valor vuelve a bajar — algo en mí que vuelve a eso.",
    ]),
    closing: localizedGenderNode([
      "Hay algo en mí que no depende solo de estas caídas.",
      "Queda un lugar pequeño de pertenencia.",
    ]),
    action: localizedGenderNode(
      "Hoy vuelvo a una cosa en mí que no me quitaron.",
      "Hoy vuelvo a una cosa en mí que no me quitaron."
    ),
  },
  {
    dayNumber: 13,
    emotionalStage: "choice",
    title: "Un paso pequeño que no sale del achicamiento",
    opening: localizedGenderNode([
      "No necesito un giro enorme ahora.",
      "Necesito un paso pequeño",
      "que no salga de que soy menos.",
      "Un paso real.",
      "Pequeño.",
      "Pero no anulado por dentro.",
    ]),
    mindfulness: localizedGenderNode([
      "Busco el paso pequeño que puedo sin achicarme en el camino.",
    ]),
    prompts: localizedGenderNode([
      "Un paso pequeño que aún cabe sin salir del achicamiento.",
      "Un miedo pequeño que se mueve alrededor de este paso.",
      "Quedarme con esto sin confianza plena — cómo se siente adentro.",
    ]),
    closing: localizedGenderNode([
      "Elegí un paso pequeño no construido sobre achicarme.",
      "Eso ya es otra dirección.",
    ]),
    action: localizedGenderNode(
      "Hoy voy con este paso pequeño aunque no esté del todo seguro.",
      "Hoy voy con este paso pequeño aunque no esté del todo segura."
    ),
  },
  {
    dayNumber: 14,
    emotionalStage: "continuation",
    title: "Seguir con un poco más de valor por dentro",
    opening: localizedGenderNode([
      "Esta sensación volverá a veces.",
      "Lo sé.",
      "Pero no tengo que caer con ella directo a \"soy menos\".",
      "Hoy tengo un poco más de camino",
      "para volver a mí incluso dentro de esto.",
    ]),
    mindfulness: localizedGenderNode([
      "Respiro y vuelvo a lo que en mí no es solo este momento.",
    ]),
    prompts: localizedGenderNode([
      "La sensación de ser menos — cómo se me presenta ahora.",
      "Primera señal de que sube — la reconozco.",
      "Una frase corta que empieza a quedarse conmigo.",
    ]),
    closing: localizedGenderNode([
      "No termina aquí.",
      "Me quedo un momento más conmigo.",
    ]),
    action: localizedGenderNode(
      "Hoy guardo una frase corta que me devuelve del achicamiento hacia el valor.",
      "Hoy guardo una frase corta que me devuelve del achicamiento hacia el valor."
    ),
  },
].map(buildNormalizedDay);

export const lowSelfEsteemNormalizedBlueprintEs = buildNormalizedPilotBlueprint({
  id: "low_self_esteem_14d",
  version: "normalizer_locale_es_v4",
  durationDays: 14,
  problemKey: "low_self_esteem",
  category: "self_worth",
  journeyName: {
    he: "ערך עצמי נמוך",
    en: "Low self-esteem",
    es: "Baja autoestima",
  },
  days,
});

export default lowSelfEsteemNormalizedBlueprintEs;
